// E8TriangleAngleConclusionsClosedTask.js
// Zadanie zamkniete E8 (2025 CZE zad. 10) - suma katow trojkata, ocena trzech wnioskow.
// Wniosek 1: prawda, Wniosek 2: falsz (istnieje trojkat rozwartokatny rownoramienny),
// Wniosek 3: prawda. Poprawna odpowiedz: "Tylko 1. i 3.".

import React from "react";
import { InlineMath, ChoiceOptionMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];
const OPTION_TEXTS = ["Tylko 1. i 3.", "Tylko 1. i 2.", "Tylko 2. i 3.", "1., 2. i 3."];

const ORIGINAL_CONCLUSIONS = [
  {
    text: "Skoro suma miar kątów wewnętrznych trójkąta jest równa 180°, to trójkąt nie może mieć dwóch kątów wewnętrznych prostych ani dwóch kątów wewnętrznych rozwartych.",
    truth: true,
    reason: "Dwa kąty proste dałyby sumę 180° już na dwóch kątach, a dwa rozwarte dałyby ponad 180°.",
  },
  {
    text: "Ponieważ trójkąt nie może mieć dwóch kątów wewnętrznych rozwartych, więc nie istnieje trójkąt rozwartokątny równoramienny.",
    truth: false,
    reason: "Istnieje trójkąt rozwartokątny równoramienny, na przykład o kątach 120°, 30°, 30°.",
  },
  {
    text: "Jeśli suma miar dwóch kątów wewnętrznych trójkąta jest większa od 90°, to trzeci kąt wewnętrzny tego trójkąta jest ostry.",
    truth: true,
    reason: "Trzeci kąt ma wtedy mniej niż 90°, więc jest ostry.",
  },
];

const RANDOM_SETS = [
  ORIGINAL_CONCLUSIONS,
  [
    {
      text: "Jeśli suma miar dwóch kątów wewnętrznych trójkąta jest równa 90°, to trzeci kąt tego trójkąta jest prosty.",
      truth: true,
      reason: "Trzeci kąt ma miarę 180° - 90° = 90°, więc jest prosty.",
    },
    {
      text: "Jeśli jeden z kątów trójkąta jest rozwarty, to każdy z dwóch pozostałych kątów jest ostry.",
      truth: true,
      reason: "Kąt rozwarty ma więcej niż 90°, więc na dwa pozostałe kąty zostaje mniej niż 90°.",
    },
    {
      text: "Jeśli suma miar dwóch kątów wewnętrznych trójkąta jest równa 90°, to trzeci kąt tego trójkąta jest rozwarty.",
      truth: false,
      reason: "W takim przypadku trzeci kąt jest prosty, a nie rozwarty.",
    },
  ],
  [
    {
      text: "Jeśli jeden z kątów trójkąta jest prosty, to dwa pozostałe kąty są ostre.",
      truth: true,
      reason: "Pozostałe dwa kąty mają razem 90°, więc każdy z nich jest mniejszy od 90°.",
    },
    {
      text: "W trójkącie może być co najwyżej jeden kąt rozwarty.",
      truth: true,
      reason: "Dwa kąty rozwarte miałyby razem więcej niż 180°.",
    },
    {
      text: "Jeśli jeden z kątów trójkąta jest rozwarty, to jeden z pozostałych kątów też może być rozwarty.",
      truth: false,
      reason: "Trójkąt nie może mieć dwóch kątów rozwartych, bo ich suma przekroczyłaby 180°.",
    },
  ],
  [
    {
      text: "Jeśli trzeci kąt trójkąta jest ostry, to suma miar dwóch pozostałych kątów jest większa od 90°.",
      truth: true,
      reason: "Skoro trzeci kąt ma mniej niż 90°, to dwa pozostałe mają razem więcej niż 90°.",
    },
    {
      text: "Jeśli trzeci kąt trójkąta jest rozwarty, to suma miar dwóch pozostałych kątów jest mniejsza od 90°.",
      truth: true,
      reason: "Kąt rozwarty ma więcej niż 90°, więc dwa pozostałe mają razem mniej niż 90°.",
    },
    {
      text: "Jeśli trzeci kąt trójkąta jest rozwarty, to suma miar dwóch pozostałych kątów jest większa od 90°.",
      truth: false,
      reason: "Dwa pozostałe kąty mają wtedy razem mniej niż 90°.",
    },
  ],
];

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function optionTextFor(conclusions) {
  const trueIndexes = conclusions
    .map((item, index) => (item.truth ? `${index + 1}.` : null))
    .filter(Boolean);
  return trueIndexes.length === 3
    ? "1., 2. i 3."
    : `Tylko ${trueIndexes.slice(0, -1).join(", ")}${trueIndexes.length > 1 ? " i " : ""}${trueIndexes.slice(-1)}`;
}

function buildData({ conclusions = ORIGINAL_CONCLUSIONS, shuffleOptions = true } = {}) {
  const correctText = optionTextFor(conclusions);
  const texts = shuffleOptions ? shuffle(OPTION_TEXTS) : OPTION_TEXTS.slice();
  const options = texts.map((text, index) => ({ text, correct: text === correctText, label: LETTERS[index] }));
  return { conclusions, correctText, options, correctIndex: options.findIndex((o) => o.correct) };
}

function createOriginalTask() {
  return {
    type: "E8TriangleAngleConclusionsClosedTask",
    data: buildData({ conclusions: ORIGINAL_CONCLUSIONS, shuffleOptions: false }),
    source: { variant: "original", exam: "E8", session: "2025_CZE", taskNumber: 10 },
    tags: ["e8", "planimetria", "katy-trojkata", "rozumowanie"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  return {
    type: "E8TriangleAngleConclusionsClosedTask",
    data: buildData({ conclusions: pick(RANDOM_SETS) }),
    source: { variant: "random", basedOn: "E8_2025_CZE_10" },
    tags: ["e8", "planimetria", "katy-trojkata", "rozumowanie"],
  };
}

function Conclusion({ conclusion, index }) {
  return (
    <div style={{ border: "1px solid currentColor", borderRadius: 8, padding: "8px 10px", opacity: 0.95 }}>
      <strong>Wniosek {index + 1}.</strong>
      <div style={{ marginTop: 4, fontSize: 14 }}>{conclusion.text}</div>
    </div>
  );
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Karol wie, że suma miar kątów wewnętrznych trójkąta jest równa{" "}
        <InlineMath math="180^{\circ}" />, i na tej podstawie zapisał trzy wnioski.
      </p>
      <div style={{ display: "grid", gap: 8, gridTemplateColumns: "1fr", margin: "10px 0" }}>
        {data.conclusions.map((conclusion, index) => (
          <Conclusion key={conclusion.text} conclusion={conclusion} index={index} />
        ))}
      </div>
      <p>Które z wniosków zapisanych przez Karola są prawdziwe? Wybierz właściwą odpowiedź spośród podanych.</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8, marginTop: 10 }}>
        {data.options.map((option, index) => (
          <div key={option.text} style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
            <InlineMath math={`${LETTERS[index]}.`} />
            <span>{option.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  return (
    <ABCDInput
      options={data.options.map((option, index) => ({ label: LETTERS[index], textNode: LETTERS[index] }))}
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer === data.correctIndex;
}

export function renderAnswer({ data }) {
  return <span style={{ display: "inline-flex", gap: 8, alignItems: "baseline" }}><InlineMath math={`${LETTERS[data.correctIndex]}.`} /><span>{data.options[data.correctIndex].text}</span></span>;
}

export function renderUserAnswer(userAnswer, { data }) {
  if (typeof userAnswer !== "number") return <i>(brak odpowiedzi)</i>;
  const option = data.options[userAnswer];
  if (!option) return <i>(nieprawidłowy wybór)</i>;
  return <ChoiceOptionMath letter={LETTERS[userAnswer]} math={`\\text{${option.text}}`} />;
}

export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      {data.conclusions.map((conclusion, index) => (
        <p key={conclusion.text}>
          <strong>Wniosek {index + 1}. — {conclusion.truth ? "prawdziwy" : "fałszywy"}.</strong>{" "}
          {conclusion.reason}
        </p>
      ))}
      <p>
        Prawdziwe są: {data.correctText} — poprawna odpowiedź to{" "}
        <InlineMath math={`\\text{${LETTERS[data.correctIndex]}}`} />.
      </p>
    </div>
  );
}
